import {
  AgBarSeriesTooltipRendererParams,
  AgCartesianAxisOptions,
  AgCartesianSeriesOptions,
  AgChartOptions,
  AgSeriesVisibilityChange,
  AgTooltipRendererResult,
} from 'ag-charts-enterprise';
import { AgCharts } from 'ag-charts-react';
import { TFunction, useTranslation } from 'translations/hooks';
import { getAgChartLocale } from 'translations/locale';

import {
  LegendFilter,
  useLegendFilter,
} from 'business/production-and-performance/pages/excavation-build-time/providers/legend-filter';
import { useProdAndPerfFilters } from 'business/production-and-performance/providers/prod-and-perf-filters-provider';
import { parseAndFormatRingDuration } from 'business/production-and-performance/services/ring-duration';
import { useSingleGraphReferenceContext } from 'business/providers/graph-reference';
import { GetExcavationAndBuildingDurationResponseDto } from 'generated/apiSchemas';
import { useChartResize } from 'technical/chart-resize';
import { hasInitialZoom } from 'technical/graph/has-initial-zoom';
import { useMediaType } from 'technical/media/hooks';
import { hoursToMilliseconds } from 'technical/time-utils/hours-to-milliseconds';
import { NumberRangeValue } from 'ui/form/number-range-input';
import {
  BUILD_COLOR,
  BUILD_COLOR_SECONDARY,
  EXCAVATION_COLOR,
  EXCAVATION_COLOR_SECONDARY,
} from 'ui/theme/colors';

import { formatGraphDataWithDuration } from './format-graph-data-with-duration';
import { getGraphMaxValue } from './get-graph-max-value';
import { getIntervalValues } from './get-interval-step';

const renderDurationTooltip = (
  { datum, yKey, xKey }: AgBarSeriesTooltipRendererParams,
  t: TFunction,
) => {
  return {
    heading: t(
      'productionAndPerformance.excavationBuildTime.graph.tooltip.heading',
      {
        ring: datum[xKey],
      },
    ),
    data: [{ label: '', value: parseAndFormatRingDuration(datum[yKey]) }],
  } satisfies AgTooltipRendererResult;
};

const excavationBuildTimeSeriesRealDurationsOptions = (
  t: TFunction,
  visibility: LegendFilter,
) =>
  [
    {
      type: 'bar',
      xKey: 'ringNumber',
      yKey: 'realExcavationDuration',
      yName: t(
        'productionAndPerformance.excavationBuildTime.graph.seriesNames.realExcavationDuration',
      ),
      stackGroup: 'Real',
      tooltip: {
        renderer: (params) => renderDurationTooltip(params, t),
      },
      visible: visibility.realExcavationDuration,
      fill: EXCAVATION_COLOR,
    },
    {
      type: 'bar',
      xKey: 'ringNumber',
      yKey: 'realBuildDuration',
      yName: t(
        'productionAndPerformance.excavationBuildTime.graph.seriesNames.realBuildDuration',
      ),
      stackGroup: 'Real',
      tooltip: {
        renderer: (params) => renderDurationTooltip(params, t),
      },
      visible: visibility.realBuildDuration,
      fill: BUILD_COLOR,
    },
  ] satisfies AgCartesianSeriesOptions[];

const excavationBuildTimeSeriesTotalDurationsOptions = (
  t: TFunction,
  visibility: LegendFilter,
) =>
  [
    {
      type: 'bar',
      xKey: 'ringNumber',
      yKey: 'totalExcavationDuration',
      yName: t(
        'productionAndPerformance.excavationBuildTime.graph.seriesNames.totalExcavationDuration',
      ),
      stackGroup: 'Total',
      tooltip: {
        renderer: (params) => renderDurationTooltip(params, t),
      },
      visible: visibility.totalExcavationDuration,
      fill: EXCAVATION_COLOR_SECONDARY,
    },
    {
      type: 'bar',
      xKey: 'ringNumber',
      yKey: 'totalBuildDuration',
      yName: t(
        'productionAndPerformance.excavationBuildTime.graph.seriesNames.totalBuildDuration',
      ),
      stackGroup: 'Total',
      tooltip: {
        renderer: (params) => renderDurationTooltip(params, t),
      },
      visible: visibility.totalBuildDuration,
      fill: BUILD_COLOR_SECONDARY,
    },
  ] satisfies AgCartesianSeriesOptions[];

const excavationBuildTimeAxesOptions = ({
  maxGraphValue,
  durationInterval,
}: {
  maxGraphValue: number;
  durationInterval: NumberRangeValue | undefined;
}) => {
  return [
    {
      type: 'category',
      position: 'bottom',
    },
    {
      type: 'number',
      position: 'left',
      label: {
        formatter: ({ value }) => parseAndFormatRingDuration(value),
      },
      nice: false,
      min: hoursToMilliseconds(durationInterval?.[0] ?? 0),
      max: hoursToMilliseconds(durationInterval?.[1]),
      interval: {
        values: getIntervalValues(maxGraphValue, durationInterval?.[1]),
      },
    },
  ] satisfies AgCartesianAxisOptions[];
};

interface ExcavationBuildTimeGraphProps {
  excavationBuildTimeGraphData: GetExcavationAndBuildingDurationResponseDto['graphData'];
}

export const ExcavationBuildTimeGraph = ({
  excavationBuildTimeGraphData,
}: ExcavationBuildTimeGraphProps) => {
  const { ref: ref } = useSingleGraphReferenceContext();
  const { t, language } = useTranslation();
  const { isMobile } = useMediaType();
  const filters = useProdAndPerfFilters();
  const { legendFilter, setLegendFilter } = useLegendFilter();

  const updateVisibility = ({ itemId, visible }: AgSeriesVisibilityChange) => {
    if (itemId && typeof itemId === 'string') {
      setLegendFilter((value) => {
        const updatedVisibility = {
          ...value,
          [itemId]: visible,
        };

        return updatedVisibility;
      });
    }
  };

  const showTotalDurations = filters?.showTotalDurations;
  const realDurationsSeries = excavationBuildTimeSeriesRealDurationsOptions(
    t,
    legendFilter,
  );
  const series = realDurationsSeries;

  if (showTotalDurations) {
    const totalDurationsSeries = excavationBuildTimeSeriesTotalDurationsOptions(
      t,
      legendFilter,
    );
    series.push(...totalDurationsSeries);
  }

  const data = formatGraphDataWithDuration(excavationBuildTimeGraphData);
  const maxGraphValue = getGraphMaxValue(data, showTotalDurations);

  const chartOptions = {
    data,
    series,
    axes: excavationBuildTimeAxesOptions({
      maxGraphValue,
      durationInterval: isMobile ? [0, 3] : filters.durationInterval,
    }),
    listeners: {
      seriesVisibilityChange: updateVisibility,
    },
    locale: {
      localeText: getAgChartLocale(language),
    },
    ...(isMobile
      ? {
          touch: { dragAction: 'drag' },
          zoom: {
            enabled: true,
            enableAxisDragging: false,
            enableTwoFingerZoom: true,
            buttons: {
              enabled: false,
            },
          },
          initialState: {
            zoom: hasInitialZoom(isMobile, filters)
              ? {
                  ratioX: {
                    start: 0,
                    end: 0.4,
                  },
                }
              : {},
          },
          contextMenu: {
            enabled: false,
          },
        }
      : {
          zoom: {
            // zoom on where the mouse is
            anchorPointX: 'pointer',
            anchorPointY: 'pointer',
          },
        }),
  } satisfies AgChartOptions;

  useChartResize(ref);

  return <AgCharts options={chartOptions} ref={ref} />;
};
